import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileForm from "@/components/profile/ProfileForm";
import StatsCard from "@/components/progress/StatsCard";
import CreditHistoryCard from "@/components/profile/CreditHistoryCard";
import NimiCreditsCard from "@/components/profile/NimiCreditsCard";
import CreditPackPurchase from "@/components/profile/CreditPackPurchase";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import ProfileLayout from "@/components/profile/ProfileLayout";
import { useProfile } from "@/hooks/useProfile";
import ActiveOffersCard from "@/components/profile/ActiveOffersCard";
import SalonWaitlistCard from "@/components/profile/SalonWaitlistCard";
import SubscriptionCard from "@/components/profile/SubscriptionCard";
import { useCreditsPurchaseEnabled } from "@/hooks/useCreditsPurchaseEnabled";
import SeriesInterestsCard from "@/components/profile/SeriesInterestsCard";
import ProfileFeedbackLink from "@/components/profile/ProfileFeedbackLink";

const Profile = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: profile, isLoading, refetch } = useProfile();
  const { isCreditsPurchaseEnabled } = useCreditsPurchaseEnabled();

  useEffect(() => {
    if (searchParams.get('creditpack') === 'success') {
      toast({
        title: "Credits Added!",
        description: "Your credits have been added successfully. Your new balance is available now.",
      });
      refetch();
      searchParams.delete('creditpack');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast, refetch]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!profile) {
    return (
      <div className="max-w-7xl mx-auto p-4 text-center">
        <h2 className="text-xl font-semibold text-foreground">Profile Not Found</h2>
        <p className="text-muted-foreground mt-2">Unable to load your profile information.</p>
      </div>
    );
  }

  const MainContent = (
    <div className="bg-card rounded-lg shadow p-6 mb-6">
      <ProfileHeader 
        email={profile?.email} 
        onLogout={handleLogout}
      />
      <ProfileForm 
        profile={profile} 
        onProfileUpdate={refetch}
      />
    </div>
  );

  const SidebarContent = (
    <>
      <div className="mb-6">
        <SubscriptionCard />
      </div>
      <div className="mb-6">
        <NimiCreditsCard />
      </div>
      <div className="mb-6">
        <ActiveOffersCard />
      </div>
      {isCreditsPurchaseEnabled && (
        <div className="mb-6">
          <CreditPackPurchase />
        </div>
      )}
      <div className="mb-6">
        <SalonWaitlistCard />
      </div>
      <div className="mb-6">
        <SeriesInterestsCard />
      </div>
      <div className="mb-6">
        <StatsCard />
      </div>
      <div className="mb-6">
        <ProfileFeedbackLink />
      </div>
    </>
  );

  return (
    <ProfileLayout
      main={MainContent}
      sidebar={SidebarContent}
      history={<CreditHistoryCard />}
    />
  );
};

export default Profile;
